import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { DontShowAgainCheckbox } from "./DontShowAgainCheckbox";
import styles from "./Controls.module.scss";

interface Props {
    currentStep: number;
    totalSteps: number;
    hasStepData: boolean;
    onPrevStep: () => void;
    onNextStep: () => void;
    onCancel: () => void;
}

export const Controls = ({ currentStep, totalSteps, hasStepData, onPrevStep, onNextStep, onCancel }: Props) => {
    const { t } = useTranslation();

    const isFirstPage = currentStep === 1;
    const isLastPage = currentStep === totalSteps;

    const onGoBack = () => {
        if (isFirstPage) {
            onCancel();
        } else {
            onPrevStep();
        }
    };

    const getNextButtonLabel = () => {
        if (isLastPage) return t("searchAssist.search");
        if (hasStepData) return t("searchAssist.step", { step: currentStep + 1 });
        return t("searchAssist.dontKnow");
    };

    /**
     * Intermediate steps navigate via an explicit onClick (type="button") rather than a
     * native form submit. The modal's form is wrapped by ui.validator, which attaches a
     * native submit listener that initializes asynchronously; a submit fired before it
     * settles is swallowed, so the first click on a freshly-rendered step was lost (TT-2703).
     * Only the final step submits the form, where field validation before search is desired.
     *
     * The forward button keeps the same position across steps, so React reuses the same DOM
     * node and only flips its `type` from "button" to "submit" when advancing to the last
     * step. Because that flip is flushed before the browser runs the click's default action,
     * the just-became-submit button would submit the form and skip the final step. Calling
     * preventDefault() on the navigation click cancels that default action defensively.
     */
    const handleContinue = (e: React.MouseEvent<HTMLButtonElement>) => {
        if (isLastPage) return;

        e.preventDefault();
        onNextStep();
    };

    return (
        <div className={classNames("modalSection", styles.controls)}>
            <button
                onClick={onGoBack}
                className={classNames("ancBtn", {
                    silver: isFirstPage,
                    iconArrowLeft: !isFirstPage,
                    icon: !isFirstPage,
                })}
                type="button"
            >
                {t(isFirstPage ? "searchAssist.cancel" : "searchAssist.back")}
            </button>
            {isLastPage && <DontShowAgainCheckbox />}
            <button
                className={classNames("ancBtn", {
                    "iconAfter iconArrowRightAfter": hasStepData && !isLastPage,
                })}
                type={isLastPage ? "submit" : "button"}
                onClick={handleContinue}
            >
                {getNextButtonLabel()}
            </button>
        </div>
    );
};
